import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Clapperboard } from "lucide-react";
import { API, getTours } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

export default function RecentAdventureAdmin() {
  const [form, setForm] = useState({ youtube_url: "", title: "", blurb: "", tour_slug: "" });
  const [ytId, setYtId] = useState("");
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    getTours().then((all) => setTours(all.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    fetch(`${API}/recent-adventure`)
      .then((r) => r.json())
      .then((d) => {
        setForm({
          youtube_url: d.youtube_id ? `https://youtu.be/${d.youtube_id}` : "",
          title: d.title || "", blurb: d.blurb || "", tour_slug: d.tour_slug || "",
        });
        setYtId(d.youtube_id || "");
        setUpdatedAt(d.updated_at || null);
      })
      .catch(() => toast.error("Could not load the current video"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const previewId = (() => {
    const m = (form.youtube_url || "").match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/);
    if (m) return m[1];
    if (/^[A-Za-z0-9_-]{6,}$/.test((form.youtube_url || "").trim())) return form.youtube_url.trim();
    return "";
  })();

  const save = async () => {
    if (!previewId) { toast.error("Enter a valid YouTube link"); return; }
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    if (!form.tour_slug) { toast.error("Choose the tour to link"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/recent-adventure`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "");
      const d = await res.json();
      setYtId(d.youtube_id || "");
      setUpdatedAt(d.updated_at || null);
      toast.success("Recent Adventure updated", { description: "Live on the homepage now." });
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>;

  return (
    <div data-testid="recent-adventure-admin" className="max-w-2xl">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 bg-moss rounded-sm flex items-center justify-center"><Clapperboard className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight leading-none">Recent Adventure Video</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">Homepage feature — swap the film & linked tour anytime</p>
        </div>
      </div>
      <p className="text-sm text-ink-secondary mt-3 mb-6">
        This video shows on the homepage just below the hero. Visitors tap play, then jump to the linked tour to book or enquire.
      </p>

      <div className="border border-sand-dark rounded-md bg-white p-5 space-y-5">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">YouTube link or ID</label>
          <input data-testid="ra-video-url" value={form.youtube_url} onChange={(e) => set("youtube_url", e.target.value)}
            placeholder="https://youtu.be/ly6sURMz9L8"
            className="mt-1.5 w-full h-11 px-3 rounded-sm border border-sand-dark text-ink text-sm font-mono focus:border-moss outline-none" />
          {previewId && (
            <div className="mt-3 aspect-video w-full max-w-sm rounded-sm overflow-hidden border border-sand-dark">
              <img src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`} alt="preview" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Title</label>
          <input data-testid="ra-video-title" value={form.title} onChange={(e) => set("title", e.target.value)}
            placeholder="Riding the Celestial Mountains"
            className="mt-1.5 w-full h-11 px-3 rounded-sm border border-sand-dark text-ink text-sm focus:border-moss outline-none" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">One-line blurb</label>
          <textarea data-testid="ra-video-blurb" value={form.blurb} onChange={(e) => set("blurb", e.target.value)} rows={2}
            placeholder="Our latest expedition through the wild passes of Kyrgyzstan."
            className="mt-1.5 w-full px-3 py-2.5 rounded-sm border border-sand-dark text-ink text-sm focus:border-moss outline-none resize-none" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Linked tour (Explore this journey →)</label>
          <select data-testid="ra-video-tour" value={form.tour_slug} onChange={(e) => set("tour_slug", e.target.value)}
            className="mt-1.5 w-full h-11 px-3 rounded-sm border border-sand-dark text-ink text-sm bg-white focus:border-moss outline-none">
            <option value="">— Select a tour —</option>
            {tours.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-5">
        <button data-testid="ra-video-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save video
        </button>
        {updatedAt && <span className="font-mono text-[11px] text-ink-muted">Last updated {new Date(updatedAt).toLocaleString()}</span>}
      </div>
    </div>
  );
}
